import { FC, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Button, Stack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { Dialog, FormField, Input } from '~/core/shared/components';
import { useFormResolver } from '~/core/shared/hooks';
import { DocumentFilter, SaveFilterSavePayload } from '~/core/shared/types';
import { handleSuccess } from '~/core/shared/utils/admin';
import { createSortFilter } from '~/core/shared/utils/queries';
import { useSaveFilterQuery } from '../../hooks/useSaveFilterQuery';
import { DocumentListContext } from '../application';

type SaveFilterFormValues = {
  name: string;
};

const validationSchema = yup.object({
  name: yup.string().trim().required(),
});

export const SaveFilterModal: FC = () => {
  const { filters, saveFilterModalApi, sorting, setChosenFilter } =
    useContext(DocumentListContext);
  const queryClient = useQueryClient();

  // TODO: @haxxxton update this to use form blocks
  const resolver = useFormResolver<SaveFilterFormValues>(validationSchema);
  const form = useForm<SaveFilterFormValues>({
    resolver,
  });

  const onSuccess = (filter: DocumentFilter): void => {
    form.reset();
    saveFilterModalApi.onClose();
    handleSuccess('Search saved');
    queryClient.invalidateQueries({ queryKey: ['filters'] });
    setChosenFilter(filter);
  };

  const { saveFilter, isPending } = useSaveFilterQuery(onSuccess);

  const onSubmit = async (formValues: SaveFilterFormValues): Promise<void> => {
    const data: SaveFilterSavePayload = {
      ...formValues,
      filter: {
        ...filters,
        ...createSortFilter(sorting),
      },
    };

    await saveFilter(data);
  };

  return (
    <Dialog
      header="Save search"
      onOpenChange={saveFilterModalApi.onOpenChange}
      open={saveFilterModalApi.open}
      size="lg"
      subHeader="Save your current search"
    >
      <FormProvider {...form}>
        <Stack as="form" gap={6} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField component={Input} label="Name" name="name" required />

          <Stack direction="row" justifyContent="end">
            <Button
              colorPalette="gray"
              fontSize="md"
              fontWeight="normal"
              onClick={saveFilterModalApi.onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              colorPalette="primary"
              fontSize="md"
              fontWeight="bold"
              loading={isPending}
              type="submit"
              variant="solid"
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  );
};
